"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "./auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export function LoginForm() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [localLoading, setLocalLoading] = useState(false);
  const { signIn, resetLoadingState } = useAuth();
  const router = useRouter();
  
  // Reset loading state and check for sign-in errors when component mounts
  useEffect(() => {
    // Clear any lingering loading state from previous sessions
    resetLoadingState();
    
    // Check for error parameter in the URL (from failed signout)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('error')) {
      const errorParam = urlParams.get('error');
      if (errorParam === 'signout_failed') {
        setError('Warning: There was an issue signing out previously. You may need to clear your browser cache.');
      }
    }
    
    // Clean up URL parameters if any exist
    if (window.history && window.history.replaceState && window.location.search) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [resetLoadingState]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLocalLoading(true);

    try {
      await signIn(username, password);

      // Set a cookie for middleware authentication check
      // In a real app, this would be handled by Amplify or your auth provider
      document.cookie =
        "amplify-authenticator-authState=authenticated; path=/; max-age=86400";

      router.push("/dashboard");
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to sign in";
      setError(errorMessage);
      setLocalLoading(false);
    }
  };

  return (
    <Card className="w-[350px] mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl font-bold">PowerProphet</CardTitle>
        <CardDescription>Wind Energy Forecasting Platform</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit}>
          <div className="grid w-full items-center gap-4">
            <div className="flex flex-col space-y-1.5">
              <Input
                id="username"
                placeholder="Email or Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col space-y-1.5">
              <Input
                id="password"
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
          </div>
          <Button className="w-full mt-6" type="submit" disabled={localLoading}>
            {localLoading ? "Signing in..." : "Sign In"}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="flex justify-center">
        <p className="text-sm text-muted-foreground">
          Secure Access for Utilities & Government Partners
        </p>
      </CardFooter>
    </Card>
  );
}
